import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  ArrowLeft,
  Database,
  FileText,
  Code,
  Target,
  BarChart3,
  Upload,
  CheckCircle,
  Circle,
  RefreshCw
} from 'lucide-react';
import { apiService } from '../services/apiService';

interface ProcessNode {
  id: string;
  name: string;
  description: string;
  icon: any;
  route: string;
  completed: boolean;
  order: number;
}

const RunFlowchartPage: React.FC = () => {
  const navigate = useNavigate();
  const { runId } = useParams<{ runId: string }>();
  const [runDetails, setRunDetails] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [processNodes, setProcessNodes] = useState<ProcessNode[]>([]);

  useEffect(() => {
    if (runId) {
      loadRunDetails();
    }
  }, [runId]);

  const loadRunDetails = async () => {
    try {
      setLoading(true);
      const details = await apiService.getRun(runId!);
      setRunDetails(details);
      buildProcessNodes(details);
    } catch (error) {
      console.error('Failed to load run details:', error);
    } finally {
      setLoading(false);
    }
  };

  const buildProcessNodes = (details: any) => {
    const nodes: ProcessNode[] = [];
    const info = details.info || {};
    const processes = info.processes || {};
    const businessLogic = info.business_logic || [];
    const hasInputData = details.input_csv_files && details.input_csv_files.length > 0;
    const hasSyntheticData = details.synthetic_csv_files && details.synthetic_csv_files.length > 0;
    const hasExportConnection = info.export_connection || processes.export || false;

    let order = 0;

    // 1. Upload/Input Data
    if (hasInputData) {
      nodes.push({
        id: 'upload',
        name: 'Upload Data',
        description: 'CSV files uploaded',
        icon: Upload,
        route: `/run/${runId}/select`, // Can go to select page to see inputs
        completed: true,
        order: order++
      });
    }

    // 2. Schema Analysis
    if (processes.analysis && processes.analysis.status === 'completed') {
      nodes.push({
        id: 'analysis',
        name: 'Schema Analysis',
        description: 'Schema analyzed and relationships detected',
        icon: FileText,
        route: `/run/${runId}/select`,
        completed: true,
        order: order++
      });
    }

    // 3. Code Generation
    if (processes.code_generation && processes.code_generation.status === 'completed') {
      nodes.push({
        id: 'code_generation',
        name: 'Code Generation',
        description: 'Faker code generated',
        icon: Code,
        route: `/run/${runId}/generate`,
        completed: true,
        order: order++
      });
    }

    // 4. Business Rules (if applied)
    if (businessLogic.length > 0) {
      nodes.push({
        id: 'business_rules',
        name: 'Business Rules',
        description: `${businessLogic.length} rule(s) applied`,
        icon: Target,
        route: `/run/${runId}/generate`, // Business rules are on generate page
        completed: true,
        order: order++
      });
    }

    // 5. Data Generation
    if (processes.data_generation && processes.data_generation.status === 'completed') {
      nodes.push({
        id: 'data_generation',
        name: 'Data Generation',
        description: 'Synthetic data generated',
        icon: BarChart3,
        route: `/run/${runId}/generate`,
        completed: true,
        order: order++
      });
    }

    // 6. Output Source Connected (if export connection exists)
    if (hasExportConnection || (processes.export && processes.export.status === 'completed')) {
      nodes.push({
        id: 'output_source',
        name: 'Output Source Connected',
        description: 'Export destination configured',
        icon: Database,
        route: `/run/${runId}/export`,
        completed: true,
        order: order++
      });
    }

    setProcessNodes(nodes);
  };

  const handleNodeClick = (node: ProcessNode) => {
    navigate(node.route);
  };

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="h-8 w-8 text-accent-600 dark:text-accent-400 animate-spin mx-auto mb-4" />
          <p className="text-windows-600 dark:text-gray-400">Loading run details...</p>
        </div>
      </div>
    );
  }

  if (!runDetails) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <Database className="h-8 w-8 text-windows-400 dark:text-gray-600 mx-auto mb-4" />
          <p className="text-windows-600 dark:text-gray-400">Run not found</p>
          <button
            onClick={() => navigate('/workspace')}
            className="btn-windows mt-4"
          >
            Back to Workspace
          </button>
        </div>
      </div>
    );
  }

  const runName = runDetails.info?.custom_name || runId || 'Unknown Run';

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="p-4 border-b border-windows-200 dark:border-gray-700 bg-white dark:bg-gray-800">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/workspace')}
              className="btn-windows flex items-center space-x-2"
            >
              <ArrowLeft className="h-4 w-4" />
              <span>Back to Workspace</span>
            </button>
            <div>
              <h1 className="text-2xl font-bold text-windows-900 dark:text-gray-100">{runName}</h1>
              <p className="text-sm text-windows-600 dark:text-gray-400 mt-1">Process Flowchart</p>
            </div>
          </div>
          <button
            onClick={loadRunDetails}
            className="btn-windows flex items-center space-x-2"
            disabled={loading}
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* Flowchart */}
      <div className="flex-1 bg-windows-50 dark:bg-gray-900 p-8 overflow-auto">
        {processNodes.length === 0 ? (
          <div className="text-center py-12">
            <Database className="h-16 w-16 text-windows-300 dark:text-gray-600 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-windows-600 dark:text-gray-400 mb-2">No Processes Yet</h3>
            <p className="text-windows-500 dark:text-gray-500 text-sm mb-6">
              Start by uploading data and running schema analysis
            </p>
            <button
              onClick={() => navigate(`/run/${runId}/select`)}
              className="btn-windows-primary"
            >
              Go to Table Selection
            </button>
          </div>
        ) : (
          <div className="max-w-full mx-auto px-4">
            <div className="flex items-center justify-center flex-wrap gap-4 pb-8">
              {processNodes.map((node, index) => (
                <React.Fragment key={node.id}>
                  {/* Process Node */}
                  <div className="flex flex-col items-center flex-shrink-0">
                    <button
                      onClick={() => handleNodeClick(node)}
                      className={`group relative flex flex-col items-center w-28 sm:w-32 p-3 rounded-lg border-2 transition-all duration-200 ${
                        node.completed
                          ? 'bg-white dark:bg-gray-800 border-accent-400 dark:border-accent-600 hover:border-accent-500 dark:hover:border-accent-500 hover:shadow-lg cursor-pointer'
                          : 'bg-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600 opacity-60 cursor-not-allowed'
                      }`}
                    >
                      {/* Status Icon */}
                      <div className={`mb-2 p-2 rounded-lg ${
                        node.completed
                          ? 'bg-accent-100 dark:bg-accent-900/30 group-hover:bg-accent-200 dark:group-hover:bg-accent-900/50'
                          : 'bg-gray-200 dark:bg-gray-600'
                      }`}>
                        <node.icon className={`h-4 w-4 sm:h-5 sm:w-5 ${
                          node.completed
                            ? 'text-accent-600 dark:text-accent-400'
                            : 'text-gray-400 dark:text-gray-500'
                        }`} />
                      </div>
                      
                      {/* Node Name */}
                      <h4 className={`text-sm font-semibold text-center mb-1 ${
                        node.completed
                          ? 'text-windows-900 dark:text-gray-100'
                          : 'text-gray-500 dark:text-gray-400'
                      }`}>
                        {node.name}
                      </h4>
                      
                      {/* Description */}
                      <p className={`text-xs text-center mb-1.5 line-clamp-2 ${
                        node.completed
                          ? 'text-windows-600 dark:text-gray-400'
                          : 'text-gray-400 dark:text-gray-500'
                      }`}>
                        {node.description}
                      </p>
                      
                      {/* Step Number */}
                      <div className={`text-xs px-1.5 py-0.5 rounded-full ${
                        node.completed
                          ? 'bg-accent-100 dark:bg-accent-900/30 text-accent-600 dark:text-accent-400'
                          : 'bg-gray-200 dark:bg-gray-600 text-gray-500 dark:text-gray-400'
                      }`}>
                        {index + 1}
                      </div>

                      {/* Completed Checkmark */}
                      {node.completed && (
                        <div className="absolute top-1.5 right-1.5">
                          <CheckCircle className="h-4 w-4 text-green-500 dark:text-green-400" />
                        </div>
                      )}
                    </button>
                  </div>
                  
                  {/* Connector Arrow */}
                  {index < processNodes.length - 1 && (
                    <div className="hidden sm:flex flex-col items-center flex-shrink-0">
                      <div className={`w-8 h-0.5 ${
                        processNodes[index + 1].completed
                          ? 'bg-accent-400 dark:bg-accent-600'
                          : 'bg-gray-300 dark:bg-gray-600'
                      }`}></div>
                      <div className={`w-0 h-0 border-l-[6px] border-r-[6px] border-t-[6px] border-transparent ${
                        processNodes[index + 1].completed
                          ? 'border-t-accent-400 dark:border-t-accent-600'
                          : 'border-t-gray-300 dark:border-t-gray-600'
                      } transform rotate-90`}></div>
                    </div>
                  )}
                </React.Fragment>
              ))}
            </div>

            {/* Summary Stats */}
            <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
              <div className="card-windows p-3 sm:p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs sm:text-sm font-medium text-windows-600 dark:text-gray-400">Total Processes</p>
                    <p className="text-xl sm:text-2xl font-bold text-windows-900 dark:text-gray-100">{processNodes.length}</p>
                  </div>
                  <Database className="h-6 w-6 sm:h-8 sm:w-8 text-accent-600 dark:text-accent-400" />
                </div>
              </div>

              <div className="card-windows p-3 sm:p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs sm:text-sm font-medium text-windows-600 dark:text-gray-400">Completed</p>
                    <p className="text-xl sm:text-2xl font-bold text-green-600 dark:text-green-400">
                      {processNodes.filter(n => n.completed).length}
                    </p>
                  </div>
                  <CheckCircle className="h-6 w-6 sm:h-8 sm:w-8 text-green-600 dark:text-green-400" />
                </div>
              </div>

              {runDetails.input_csv_files && (
                <div className="card-windows p-3 sm:p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs sm:text-sm font-medium text-windows-600 dark:text-gray-400">Input Tables</p>
                      <p className="text-xl sm:text-2xl font-bold text-windows-900 dark:text-gray-100">
                        {runDetails.input_csv_files.length}
                      </p>
                    </div>
                    <FileText className="h-6 w-6 sm:h-8 sm:w-8 text-blue-600 dark:text-blue-400" />
                  </div>
                </div>
              )}

              {runDetails.synthetic_csv_files && runDetails.synthetic_csv_files.length > 0 && (
                <div className="card-windows p-3 sm:p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs sm:text-sm font-medium text-windows-600 dark:text-gray-400">Generated Files</p>
                      <p className="text-xl sm:text-2xl font-bold text-windows-900 dark:text-gray-100">
                        {runDetails.synthetic_csv_files.length}
                      </p>
                    </div>
                    <BarChart3 className="h-6 w-6 sm:h-8 sm:w-8 text-purple-600 dark:text-purple-400" />
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RunFlowchartPage;

